<script setup lang="ts">
import { onMounted, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { ReduktionWaermeerzeuger, ReduktionWaermeerzeugerCollection } from '@/models/ReduktionWaermeerzeuger';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'waermeerzeuger_heizung' },
    { direction: 'asc', property: 'bauperiode_code' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new ReduktionWaermeerzeugerCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});

function editRecord(r: ReduktionWaermeerzeuger) {
    router.push({ name: 'bg_geb_reduktion_waermeerzeuger_edit', params: { id: r?.id } });
}

async function deleteRecord(r: ReduktionWaermeerzeuger) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_geb_reduktion_waermeerzeuger_edit', params: { id: 'new' } });
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            :title="$t('reduktion_waermeerzeuger')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as ReduktionWaermeerzeuger)"
            @deleterecord="deleteRecord($event as ReduktionWaermeerzeuger)"
            @new="newRecord"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column field="bauperiode" sortable :header="$t('bauperiode')" style="width: 5rem"
                >{
                <template #body="slotProps">
                    <div>{{ $t(slotProps.data.bauperiode_i18n_key) }}</div>
                </template>
                }</Column
            >
            <Column
                field="waermeerzeuger_heizung"
                sortable
                :header="$t('waermeerzeuger_heizung')"
                style="width: 5rem"
            ></Column>
            <Column field="reduktion" sortable :header="$t('reduktion')"></Column>
        </KpBaseCollectionGrid>
    </div>
</template>
